import { RefObject, useCallback, useRef } from 'react';

import { AnalyticsEvent, emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

const defaultOptions: ScrollIntoViewOptions = {
  behavior: 'smooth',
};

/**
 * Custom hook to manage scrolling to components
 * Provides a ref to attach to a component and a scroll function to trigger the scroll.
 *
 * @param options - ScrollIntoView options for controlling scroll behavior (defaults to smooth scrolling)
 * @returns Object containing componentRef (to attach to target element) and scrollToComponent (function to trigger scroll)
 */
export function useScrollToComponent<T extends HTMLElement = HTMLDivElement>(
  options: ScrollIntoViewOptions = defaultOptions
): {
  componentRef: RefObject<T | null>;
  scrollToComponent: (analyticsEvent?: AnalyticsEvent) => void;
} {
  const componentRef = useRef<T>(null);

  const scrollToComponent = useCallback(
    (analyticsEvent?: AnalyticsEvent) => {
      if (componentRef.current) {
        componentRef.current.scrollIntoView(options);
        if (analyticsEvent) emitAnalyticsEvent(analyticsEvent);
      }
    },
    [options]
  );

  return {
    componentRef,
    scrollToComponent,
  };
}
